import { getBondingTokenPrice } from '@cronos-app/api';
import type { ChainId, ExplicitAddress } from '@cronos-app/sdk';
import { useDesiredChain } from '@cronos-app/wallet';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { type NormalToken } from '@ui/config/token/types';
import { RefetchInterval } from '@ui/config/ui';
import type { Fraction } from 'bi-fraction';

import { useGlobalChainLayer } from '../useGlobalChainLayer';
import { fetchTokenPrice } from './fetchers/priceFetchers';
import type { AgentTokenWithAddress, BondingTokenWithAddress } from './types';
import { isBondingToken } from './types';

export const useTokenPrice = ({
  token,
  ...options
}: Partial<
  UseQueryOptions<{
    usd_price: Fraction;
    usd_price_change_24h: Fraction;
  } | null>
> & {
  token?: NormalToken | BondingTokenWithAddress | AgentTokenWithAddress;
}) => {
  const chainLayer = useGlobalChainLayer();
  const chainId = useDesiredChain(chainLayer);

  return useQuery({
    queryKey: getTokenPriceQueryKey(token, chainId),
    queryFn: async () => {
      if (!token) throw new Error('token is required');
      return fetchTokenPrice(token, chainId);
    },
    staleTime: RefetchInterval.Slow,
    ...options,
    enabled: options.enabled === false ? false : token !== undefined,
  });
};

export const useBondingTokenOrAgentTokenPrice = ({
  bondingAddress,
  currency,
  ...options
}: Partial<
  UseQueryOptions<{
    usd_price: Fraction;
    usd_price_change_24h: Fraction;
  } | null>
> & {
  bondingAddress: ExplicitAddress;
  currency?: Parameters<typeof getBondingTokenPrice>[2];
}) => {
  const chainLayer = useGlobalChainLayer();

  return useQuery({
    queryKey: [
      'useBondingTokenOrAgentTokenPrice',
      bondingAddress,
      currency,
      chainLayer,
    ],
    queryFn: async () => {
      if (!bondingAddress) throw new Error('bondingAddress is required');

      return getBondingTokenPrice(chainLayer, bondingAddress, currency);
    },
    ...options,
    enabled: options.enabled === false ? false : !!bondingAddress,
  });
};

export const getTokenPriceQueryKey = (
  token?: NormalToken | BondingTokenWithAddress | AgentTokenWithAddress,
  chainId?: ChainId,
) => {
  if (token && isBondingToken(token)) {
    return [
      'useTokenPrice',
      token.type,
      token.bondingAddress,
      chainId,
    ] as const;
  }
  return ['useTokenPrice', token, chainId] as const;
};
